import { Injectable } from '@angular/core';

import { SearchBarState, SortQualifier } from '@core/filters/path-filter/dsl/search-bar';
import { FilterExpr } from '@core/filters/path-filter/wire';
import { FilterGrammar } from '@core/store/user-preferences/user-preferences.model';

import { FilterConfiguration } from '@shared/components/filter-query-builder/filter.models';
import { SearchResult } from '@shared/components/search-query-builder/search-query-builder.component';
import { RestoredSort } from '@shared/utils/filter-preset.utils';

/** The list context a snapshot belongs to. A snapshot only restores into its own context. */
export interface EntityListFilterContext {
  entityType: string;
  dataspaceId: string | null;
  planId: string | null;
}

/** A Path filter as the composer and the search bar hold it. */
export interface PathFilterSnapshot {
  filter?: FilterExpr;
  sort: SortQualifier[];
  bar: SearchBarState;
}

/**
 * The filtering state of an entity list, as the UI holds it.
 *
 * Deliberately the raw signal values rather than the reduced `(filters, search, sort)`
 * triple a preset stores: that reduction turns search-bar qualifiers into filter
 * conditions, which would move the user's text out of the search box and into the
 * advanced filter panel on the way back.
 */
export interface EntityListFilterSnapshot extends EntityListFilterContext {
  searchResult: SearchResult | null;
  searchQuery: string;
  advancedFilters: FilterConfiguration | null;
  /** Sort with `SortState`'s generic erased, the shape a restored preset also carries. */
  sort: RestoredSort[];
  showAdvancedFiltering: boolean;
  activePresetId: number | null;
  /** A preset-chosen grammar, in either direction: a Classic preset holds a Path user in Classic. */
  presetGrammar: FilterGrammar | null;
  path?: PathFilterSnapshot | null;
}

/**
 * Carries an entity list's filtering state across the edit round-trip.
 *
 * Editing a row leaves the list route, so Angular destroys `EntityComponent` and the
 * `FilterPresetsStore` it provides; returning from the form mounts a fresh instance with
 * empty filters. This service is the only thing that outlives both, so the list can stash
 * its state on the way out and pick it up on the way back.
 *
 * The snapshot is consumed once, which bounds the feature to that round-trip: reaching the
 * list by any other path still starts clean (or from the default preset).
 */
@Injectable({ providedIn: 'root' })
export class EntityListFilterHandoffService {
  private pending: EntityListFilterSnapshot | null = null;

  stash(snapshot: EntityListFilterSnapshot): void {
    this.pending = snapshot;
  }

  /**
   * Hands over the pending snapshot when it belongs to `context`, and drops it either way.
   *
   * Dropping a snapshot that does not match matters as much as returning one that does: an
   * edit form abandoned towards another entity would otherwise leave a snapshot behind that
   * resurfaces on some unrelated list later in the session.
   */
  consume(context: EntityListFilterContext): EntityListFilterSnapshot | null {
    const snapshot = this.pending;
    this.pending = null;
    if (
      snapshot === null ||
      snapshot.entityType !== context.entityType ||
      snapshot.dataspaceId !== context.dataspaceId ||
      snapshot.planId !== context.planId
    ) {
      return null;
    }
    return snapshot;
  }
}
